"use client"

import { Dialog, Transition } from "@headlessui/react"
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline"
import { V2ProjectId } from "@researchwiseai/identities"
import { Fragment, useCallback, useRef, useState } from "react"
import { trpc } from "../../trpc/trpc"

interface Props {
    visible: boolean
    setVisible: (visible: boolean) => void
    projectId: V2ProjectId
}

export function ProjectDeleteModal({ projectId, visible, setVisible }: Props) {
    const cancelButtonRef = useRef(null)
    const projectDeleter = trpc.projects.delete.useMutation()
    const [working, setWorking] = useState(false)

    const onConfirm = useCallback(() => {
        setWorking(true)
        projectDeleter
            .mutateAsync({
                projectId,
            })
            .then(() => {
                console.log("Project deleted")
                window.location.reload()
            })
    }, [projectDeleter, projectId])

    return (
        <Transition.Root show={visible} as={Fragment}>
            <Dialog as="div" className="relative z-10" initialFocus={cancelButtonRef} onClose={setVisible}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                {!working && (
                                    <>
                                        <div className="sm:flex sm:items-start">
                                            <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                                                <ExclamationTriangleIcon
                                                    className="size-6 text-red-600"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                                <Dialog.Title
                                                    as="h3"
                                                    className="text-base leading-6 font-semibold text-gray-900"
                                                >
                                                    Delete project
                                                </Dialog.Title>
                                                <div className="mt-2">
                                                    <p className="text-sm text-gray-500">
                                                        Are you sure you want to delete this project? All of the data
                                                        will be permanently removed from our servers forever. This
                                                        action cannot be undone.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                                            <button
                                                type="button"
                                                className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                                onClick={onConfirm}
                                            >
                                                Delete
                                            </button>
                                            <button
                                                type="button"
                                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                                onClick={() => setVisible(false)}
                                                ref={cancelButtonRef}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </>
                                )}
                                {working && (
                                    <div className="flex flex-col items-center">
                                        <div className="flex animate-spin space-x-2 p-10">
                                            <div className="bg-primary-600 size-4 rounded-full"></div>
                                            <div className="bg-primary-600 size-4 rounded-full"></div>
                                            <div className="bg-primary-600 size-4 rounded-full"></div>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-500">Deleting project, please wait</p>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
